/**
 * Better Auth browser client.
 *
 * Cookie-based sessions — no manual token handling needed.
 */
import { createAuthClient } from 'better-auth/vue';

const API_BASE = (import.meta.env.VITE_BACKEND_URL as string | undefined) || 'http://localhost:5001';

export const authClient = createAuthClient({
  baseURL: `${API_BASE}/api/auth`,
});

/** Sign in with email and password. */
export async function login(email: string, password: string): Promise<void> {
  const { error } = await authClient.signIn.email({
    email,
    password,
  });
  if (error) throw new Error(error.message ?? 'Login failed');
}

/** Sign out the current user. */
export async function logout(): Promise<void> {
  await authClient.signOut();
}

/** Returns true if there is a currently authenticated user session. */
export async function isAuthenticated(): Promise<boolean> {
  const { data } = await authClient.getSession();
  return Boolean(data?.user);
}

// Better Auth's Vue client types resolve to `any` when no explicit plugin list
// is passed to createAuthClient. Re-typing against a minimal interface recovers
// narrow types without pulling in the rest of Better Auth's generic machinery.
type AuthResult = { error: { message?: string } | null };
interface PasswordAuthMethods {
  requestPasswordReset: (args: { email: string }) => Promise<AuthResult>;
  resetPassword: (args: { token: string; newPassword: string }) => Promise<AuthResult>;
}
const passwordAuth = authClient as unknown as PasswordAuthMethods;

/**
 * Request a password reset email. The server builds the link itself: the reset page
 * with the token as a query string (`resetPasswordLink` in the backend), which then
 * calls `resetPassword` below.
 */
export async function forgotPassword(email: string): Promise<void> {
  const { error } = await passwordAuth.requestPasswordReset({ email });
  if (error) throw new Error(error.message);
}

/** Complete the reset flow — `token` comes from the reset-email link. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const { error } = await passwordAuth.resetPassword({ token, newPassword });
  if (error) throw new Error(error.message);
}
